import apiService from './api'

export interface Course {
  _id: string
  schoolId: string
  name: string
  slug: string
  type: 'COURSE' | 'RETREAT' | 'WORKSHOP' | 'TRAINING' | 'CLASS'
  description: string
  shortDescription?: string
  price?: number // Optional - pricing is set per room type in accommodation
  currency: string
  duration: number // in days
  maxStudents?: number
  minStudents?: number
  difficulty: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'ALL_LEVELS'
  schedule?: {
    startDate: string
    endDate: string
    timeSlots?: {
      day: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY'
      startTime: string
      endTime: string
    }[]
  }
  inclusions?: string[]
  requirements?: string[]
  accommodation?: {
    available: boolean
      rooms: {
        roomId?: string // Reference to Room model
        type: 'PRIVATE' | 'DOUBLE' | 'TRIPLE' | 'QUADRUPLE' | 'CUSTOM'
        capacity: number
        price: number
        amenities: string[]
        description?: string
        images?: string[]
        videos?: string[]
      }[]
  }
  rating: number
  totalReviews: number
  totalBookings: number
  yogaAllianceCertified?: boolean
  isActive: boolean
  isFeatured: boolean
  createdAt: string
  updatedAt: string
}

export interface CourseForm {
  schoolId: string
  name: string
  type: 'COURSE' | 'RETREAT' | 'WORKSHOP' | 'TRAINING' | 'CLASS'
  description: string
  shortDescription?: string
  price?: number // Optional - pricing is set per room type in accommodation
  currency: string
  duration: number // in days
  maxStudents?: number
  minStudents?: number
  difficulty?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'ALL_LEVELS'
  schedule?: {
    startDate: string
    endDate: string
    timeSlots?: {
      day: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY'
      startTime: string
      endTime: string
    }[]
  }
  inclusions?: string[]
  requirements?: string[]
  accommodation?: {
    available: boolean
      rooms: {
        roomId?: string // Reference to Room model
        type: 'PRIVATE' | 'DOUBLE' | 'TRIPLE' | 'QUADRUPLE' | 'CUSTOM'
        capacity: number
        price: number
        amenities: string[]
        description?: string
        images?: string[]
        videos?: string[]
      }[]
  }
  isActive?: boolean
  isFeatured?: boolean
  yogaAllianceCertified?: boolean
}

class CoursesService {
  /**
   * Get all courses for the school
   */
  async getCourses(params?: {
    page?: number
    limit?: number
    sort?: string
    order?: 'asc' | 'desc'
    search?: string
    type?: string
    difficulty?: string
    isActive?: boolean
  }): Promise<{ success: boolean; data: { courses: Course[]; pagination: any } }> {
    try {
      const queryParams = new URLSearchParams()
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            queryParams.append(key, value.toString())
          }
        })
      }

      const response = await apiService.get(`/courses/my-school?${queryParams}`)
      return response
    } catch (error) {
      console.error('Error fetching courses:', error)
      throw error
    }
  }

  /**
   * Get course by ID
   * Backend returns: { success: true, data: { course: Course } }
   */
  async getCourseById(courseId: string): Promise<{ success: boolean; data: Course | { course: Course } }> {
    try {
      const response = await apiService.get(`/courses/${courseId}`)
      return response
    } catch (error) {
      console.error('Error fetching course:', error)
      throw error
    }
  }

  /**
   * Create a new course
   */
  async createCourse(courseData: CourseForm): Promise<{ success: boolean; data: Course }> {
    try {
      const response = await apiService.post('/courses', courseData)
      return response
    } catch (error) {
      console.error('Error creating course:', error)
      throw error
    }
  }

  /**
   * Update course
   */
  async updateCourse(courseId: string, courseData: Partial<CourseForm>): Promise<{ success: boolean; data: Course }> {
    try {
      const response = await apiService.put(`/courses/${courseId}`, courseData)
      return response
    } catch (error) {
      console.error('Error updating course:', error)
      throw error
    }
  }

  /**
   * Delete course
   */
  async deleteCourse(courseId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.delete(`/courses/${courseId}`)
      return response
    } catch (error) {
      console.error('Error deleting course:', error)
      throw error
    }
  }

  /**
   * Toggle course status (active/inactive)
   */
  async toggleCourseStatus(courseId: string): Promise<{ success: boolean; data: Course }> {
    try {
      const response = await apiService.patch(`/courses/${courseId}/status`)
      return response
    } catch (error) {
      console.error('Error toggling course status:', error)
      throw error
    }
  }

  /**
   * Toggle course featured status
   */
  async toggleCourseFeatured(courseId: string): Promise<{ success: boolean; data: Course }> {
    try {
      const response = await apiService.patch(`/courses/${courseId}/feature`)
      return response
    } catch (error) {
      console.error('Error toggling course featured:', error)
      throw error
    }
  }


  /**
   * Upload file directly to S3 using signed URL with progress tracking
   */
  async uploadFileToS3(
    file: File,
    uploadUrl: string,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const progress = Math.round((e.loaded * 100) / e.total)
          onProgress(progress)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve()
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed: Network error'))
      })

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'))
      })

      xhr.open('PUT', uploadUrl)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)
    })
  }

  /**
   * Upload course image using global media service
   * Returns fileKey which should be stored in course.media.images array
   */
  async uploadCourseImage(
    file: File,
    schoolId: string,
    courseId?: string,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; data: { fileKey: string } }> {
    try {
      // Import mediaService dynamically to avoid circular dependencies
      const mediaService = (await import('./mediaService')).default

      // Use global media service
      const result = await mediaService.uploadFile(
        file,
        'courseImage',
        schoolId,
        courseId,
        onProgress
      )

      return {
        success: true,
        data: { fileKey: result.data.fileKey }
      }
    } catch (error) {
      console.error('Error uploading course image:', error)
      throw error
    }
  }

  /**
   * Upload course video using global media service
   * Returns fileKey which should be stored in course.media.videos array
   */
  async uploadCourseVideo(
    file: File,
    schoolId: string,
    courseId?: string,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; data: { fileKey: string } }> {
    try {
      // Import mediaService dynamically to avoid circular dependencies
      const mediaService = (await import('./mediaService')).default

      // Use global media service
      const result = await mediaService.uploadFile(
        file,
        'courseVideo',
        schoolId,
        courseId,
        onProgress
      )

      return {
        success: true,
        data: { fileKey: result.data.fileKey }
      }
    } catch (error) {
      console.error('Error uploading course video:', error)
      throw error
    }
  }

  /**
   * Delete course image
   */
  async deleteCourseImage(courseId: string, imageUrl: string): Promise<{ success: boolean }> {
    try {
      const response = await apiService.delete(`/courses/${courseId}/images`, {
        data: { imageUrl }
      })
      return response
    } catch (error) {
      console.error('Error deleting course image:', error)
      throw error
    }
  }

  /**
   * Delete course video
   */
  async deleteCourseVideo(courseId: string, videoUrl: string): Promise<{ success: boolean }> {
    try {
      const response = await apiService.delete(`/courses/${courseId}/videos`, {
        data: { videoUrl }
      })
      return response
    } catch (error) {
      console.error('Error deleting course video:', error)
      throw error
    }
  }

  /**
   * Get course statistics
   */
  async getCourseStats(): Promise<{ success: boolean; data: any }> {
    try {
      const response = await apiService.get('/courses/stats')
      return response
    } catch (error) {
      console.error('Error fetching course stats:', error)
      throw error
    }
  }

  /**
   * Get course applications
   */
  async getCourseApplications(courseId: string): Promise<{ success: boolean; data: any[] }> {
    try {
      const response = await apiService.get(`/courses/${courseId}/applications`)
      return response
    } catch (error) {
      console.error('Error fetching course applications:', error)
      throw error
    }
  }

  /**
   * Duplicate course
   */
  async duplicateCourse(courseId: string): Promise<{ success: boolean; data: Course }> {
    try {
      const response = await apiService.post(`/courses/${courseId}/duplicate`)
      return response
    } catch (error) {
      console.error('Error duplicating course:', error)
      throw error
    }
  }
}

export default new CoursesService()
